import { baseApi } from 'src/Services/api'
import { Model } from 'src/Redux/api/modelApi'

import { PaginatedResponse } from '../types'

export enum DriveTypeEnum {
  CRAWLER = 'CRAWLER',
  WHEEL = 'WHEEL',
}

export interface Equipment {
  color?: string
  createdAt: string
  engineNumber?: string
  id: number
  passportNumber?: string
  registrationNumber?: string
  year?: number
  vin?: string
  estValue?: number
  isOwned?: boolean
  imageUrl?: string
  model: Model
}

export interface EquipmentQueryParams {
  page?: number
  limit?: number
  name?: string
  typeId?: string
  modelId?: number
  isOwned?: string
}

export interface AddEquipmentBody {
  modelId?: string
  year: number
  registrationNumber?: string
  engineNumber?: string
  color?: string
  vin?: string
  passportNumber?: string
  estValue?: number
  isOwned?: boolean
  name?: string
  type?: string
  driveType?: DriveTypeEnum
  baseRentalCost?: number
  fuelEfficiencySummer?: number
  fuelEfficiencyWinter?: number
  engineCapacity?: number
  description?: string
  image?: File
  operationalLogs?: string
}

export interface UpdateEquipmentBody {
  equipmentId?: string
  year?: number
  registrationNumber?: string
  engineNumber?: string
  color?: string
  vin?: string
  passportNumber?: string
  estValue?: number
  isOwned?: boolean
  name?: string
  type?: string
  driveType?: DriveTypeEnum
  baseRentalCost?: number
  fuelEfficiencySummer?: number
  fuelEfficiencyWinter?: number
  engineCapacity?: number
  description?: string
  image?: File
  operationalLogs?: string
}

const ALL_EQUIPMENT_ITEMS_PAGE_SIZE = 999

const getEquipmentQueryParams = (params: EquipmentQueryParams): string => {
  const queryParams = new URLSearchParams()
  if (params.page) queryParams.append('page', params.page.toString())
  queryParams.append('limit', ALL_EQUIPMENT_ITEMS_PAGE_SIZE.toString())
  if (params.name) queryParams.append('name', params.name)
  if (params.typeId) queryParams.append('typeId', params.typeId.toString())
  if (params.modelId) queryParams.append('modelId', params.modelId.toString())
  if (params.isOwned) queryParams.append('isOwned', params.isOwned)

  return queryParams.toString()
}

export const equipmentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllEquipment: builder.query<
      PaginatedResponse<Equipment[]>,
      EquipmentQueryParams | void
    >({
      query: (params) => {
        const queryString = params ? getEquipmentQueryParams(params) : ''
        return `/equipment?${queryString}`
      },
      providesTags: ['transport'],
    }),

    addEquipment: builder.mutation<Equipment, AddEquipmentBody>({
      query: (data) => {
        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === 'image' && value instanceof File) {
              formData.append(key, value)
            } else {
              formData.append(key, String(value))
            }
          }
        })
        return {
          url: `/equipment`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: ['transport'],
    }),

    updateEquipment: builder.mutation<
      Equipment,
      { id: number; data: UpdateEquipmentBody }
    >({
      query: ({ id, data }) => {
        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === 'image' && value instanceof File) {
              formData.append(key, value)
            } else {
              formData.append(key, String(value))
            }
          }
        })
        return {
          url: `/equipment/${id}`,
          method: 'PUT',
          body: formData,
        }
      },
      invalidatesTags: ['transport', 'models'],
    }),

    archiveEquipment: builder.mutation<void, number>({
      query: (id) => ({
        url: `/equipment/${id}/archive`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['transport'],
    }),

    restoreEquipment: builder.mutation<void, number>({
      query: (id) => ({
        url: `/equipment/${id}/restore`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['transport'],
    }),
  }),
  overrideExisting: true,
})

export const {
  useAddEquipmentMutation,
  useUpdateEquipmentMutation,
  useArchiveEquipmentMutation,
  useRestoreEquipmentMutation,
  useGetAllEquipmentQuery,
} = equipmentApi
